// Mocks for the org-level "Configurations" settings page (API Keys,
// Webhooks, WhatsApp Integration) so those tabs are usable in demo mode
// instead of falling through to a real (and failing) network request.

import { OrgAppType } from '@features/settings/types';

// ── API Keys / SDK Keys ──

type MockApiKey = {
  id: string;
  api_key_name: string;
  api_key: string;
  created_at: string;
  type: OrgAppType;
};

// API keys and SDK keys share the org-app endpoints and are told apart by
// their `type` (query param on GET, body field on POST).
let apiKeyCount = 0;
let apiKeys: MockApiKey[] = [
  {
    id: 'trial-apikey-001',
    api_key_name: 'Website Integration',
    api_key: 'cofee_live_9f2a7b3c1d4e5f6a7b8c9d0e1f2a3b4c',
    created_at: new Date().toISOString(),
    type: OrgAppType.API_INTEGRATION
  }
];

export const getAllApiKeysMock = (type: string | null) => {
  const items = apiKeys.filter((key) => !type || key.type === type);
  return { data: { items, total_count: items.length } };
};

export const createApiKeyMock = (body: {
  api_key_name?: string;
  type?: OrgAppType;
}) => {
  apiKeyCount += 1;
  const type = body?.type ?? OrgAppType.API_INTEGRATION;
  const newKey: MockApiKey = {
    id: `trial-apikey-${String(apiKeyCount + 1).padStart(3, '0')}`,
    api_key_name:
      body?.api_key_name ||
      (type === OrgAppType.SDK ? 'New SDK Key' : 'New API Key'),
    api_key: `cofee_live_${Math.abs(apiKeyCount * 2654435761).toString(16)}`,
    created_at: new Date().toISOString(),
    type
  };
  apiKeys = [newKey, ...apiKeys];
  return { data: { api_key: newKey.api_key } };
};

export const updateApiKeyMock = (
  keyId: string,
  body: { display_name?: string }
) => {
  apiKeys = apiKeys.map((key) =>
    key.id === keyId
      ? { ...key, api_key_name: body?.display_name || key.api_key_name }
      : key
  );
  return { data: {} };
};

export const deleteApiKeyMock = (keyId: string) => {
  apiKeys = apiKeys.filter((key) => key.id !== keyId);
  return { data: {} };
};

// ── Webhooks ──

type MockWebhook = {
  id: string;
  name: string;
  created_at: string;
  webhook_config: {
    url: string;
    custom_headers: Record<string, string>;
    signing_secret: string;
  };
};

let webhookCount = 0;
let mockWebhooks: MockWebhook[] = [];

const SUPPORTED_WEBHOOK_EVENTS = [
  'payment.success',
  'payment.failed',
  'member.created',
  'group.created'
];

export const getAllWebhooksMock = () => ({
  data: { items: mockWebhooks, total_count: mockWebhooks.length }
});

export const getAllSupportedEventsMock = () => ({
  data: SUPPORTED_WEBHOOK_EVENTS.map((name) => ({ name }))
});

export const createWebhookMock = (body: {
  name?: string;
  webhook_config?: { url?: string; custom_headers?: Record<string, string> };
}) => {
  webhookCount += 1;
  const newWebhook: MockWebhook = {
    id: `trial-webhook-${String(webhookCount).padStart(3, '0')}`,
    name: body?.name || 'New Webhook',
    created_at: new Date().toISOString(),
    webhook_config: {
      url: body?.webhook_config?.url || '',
      custom_headers: body?.webhook_config?.custom_headers || {},
      signing_secret: `whsec_${Math.abs(webhookCount * 2654435761).toString(
        16
      )}`
    }
  };
  mockWebhooks = [newWebhook, ...mockWebhooks];
  return { code: 'success', data: newWebhook };
};

export const getWebhookByIdMock = (webhookId: string) => ({
  data: mockWebhooks.find((w) => w.id === webhookId)
});

export const updateWebhookMock = (
  webhookId: string,
  body: {
    name?: string;
    webhook_config?: { url?: string; custom_headers?: Record<string, string> };
  }
) => {
  mockWebhooks = mockWebhooks.map((w) =>
    w.id === webhookId
      ? {
          ...w,
          name: body?.name || w.name,
          webhook_config: {
            ...w.webhook_config,
            url: body?.webhook_config?.url || w.webhook_config.url,
            custom_headers:
              body?.webhook_config?.custom_headers ||
              w.webhook_config.custom_headers
          }
        }
      : w
  );
  return { code: 'success' };
};

export const deleteWebhookMock = (webhookId: string) => {
  mockWebhooks = mockWebhooks.filter((w) => w.id !== webhookId);
  return { code: 'success' };
};

// ── WhatsApp providers ──

const SUPPORTED_PROVIDERS = [
  {
    id: 'trial-provider-gupshup',
    provider_code: 'gupshup',
    provider_name: 'Gupshup',
    image_url: '',
    webhook_url: 'https://api.dev.cofee.life/webhooks/gupshup',
    configuration: [
      { key: 'api_key', isMandatory: true, sensitive: true },
      { key: 'app_name', isMandatory: true, sensitive: false }
    ]
  },
  {
    id: 'trial-provider-interakt',
    provider_code: 'interakt',
    provider_name: 'Interakt',
    image_url: '',
    webhook_url: 'https://api.dev.cofee.life/webhooks/interakt',
    configuration: [
      { key: 'api_key', isMandatory: true, sensitive: true },
      { key: 'phone_number_id', isMandatory: false, sensitive: false }
    ]
  }
];

const SAMPLE_TEMPLATES = [
  {
    id: 'trial-template-payment-reminder',
    template_id: '',
    metadata: {
      template_label: 'Payment Reminder',
      content: 'Hi {{1}}, your payment of {{2}} is due on {{3}}.',
      sample_values: ['Aarav', '₹1,500', '5 Aug 2026'],
      buttons: [{ text: 'Pay Now' }]
    }
  },
  {
    id: 'trial-template-payment-success',
    template_id: '',
    metadata: {
      template_label: 'Payment Confirmation',
      content: 'Hi {{1}}, we received your payment of {{2}}. Thank you!',
      sample_values: ['Aarav', '₹1,500'],
      buttons: []
    }
  }
];

type MockProviderConfig = {
  id: string;
  name: string;
  notification_provider_config_id: string;
  configuration: { key: string; value: string; sensitive: boolean }[];
};

let mockProviderConfigs: MockProviderConfig[] = [];
let mockTemplateFills: {
  notification_template_id: string;
  provider_template_name: string;
}[] = [];

export const getProvidersMock = () => SUPPORTED_PROVIDERS;

export const getSampleTemplatesMock = () => SAMPLE_TEMPLATES;

export const updateProviderMock = (body: {
  provider_code?: string;
  name?: string;
  configuration?: { key: string; value: string; sensitive: boolean }[];
}) => {
  const provider = SUPPORTED_PROVIDERS.find(
    (p) => p.provider_code === body?.provider_code
  );
  mockProviderConfigs = [
    {
      id: `trial-provider-config-${body?.provider_code}`,
      name: body?.name || provider?.provider_name || '',
      notification_provider_config_id: provider?.id || '',
      configuration: body?.configuration || []
    }
  ];
  return { data: mockProviderConfigs };
};

export const getProviderConfigMock = () => mockProviderConfigs;

export const updateTemplateConfigMock = (body: {
  templates?: {
    notification_template_id: string;
    provider_template_name: string;
  }[];
}) => {
  const incoming = body?.templates || [];
  const incomingIds = new Set(incoming.map((t) => t.notification_template_id));
  mockTemplateFills = [
    ...mockTemplateFills.filter(
      (t) => !incomingIds.has(t.notification_template_id)
    ),
    ...incoming
  ];
  return { data: {} };
};

export const getTemplateConfigMock = () => ({ templates: mockTemplateFills });
